// AppErrorBoundary — last-resort React error boundary (S12 Lane E, the Stage-0 P0).
//
// Why: a render-time crash anywhere in the tree (the S0-17 class — a Rules-of-Hooks
// violation on a detail screen) used to take down the WHOLE app to a blank white page,
// with no way out but a manual reload. React only recovers from render errors via a
// class component implementing getDerivedStateFromError / componentDidCatch, so this
// is intentionally a class (the one place the codebase needs one).
//
// On catch it renders the Make-aligned LxErrorState primitive (NOT a hand-rolled
// screen — D-185 / D-ALL-VISUAL-VIA-MAKE-01: the error surface reuses the existing
// designed primitive) with a reload-to-recover action, instead of a white screen.
//
// Placement: wraps BOTH entry paths in main.tsx (App + DevPreview). A render crash in
// the routed content therefore shows this fallback rather than a blank document.
//
// D-NUMBERS-EN-01: no numeric content. D-AR-FIRST: Arabic is the default surface
// (locale defaults to 'ar' since the boundary sits above the locale context).

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { LxErrorState } from '@lamtix/ui';
import { reportRenderCrash } from '@/lib/error-reporting';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /** Locale for the fallback copy. Defaults to 'ar' (D-AR-FIRST) — the boundary sits
   *  above the app's locale context, so the real locale may be unavailable here. */
  locale?: 'ar' | 'en';
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for diagnosis — never swallow silently (S0-20 lesson: silent failures
    // hide real defects). The browser console + any error-reporting hook can pick this up.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] uncaught render error:', error, info.componentStack);

    // ...and durably, because a console line lives on the USER's machine and nobody will ever
    // read it. reportRenderCrash sends the route PATTERN, the innermost component NAME and the
    // error NAME — never the message, and never the componentStack, which can carry props
    // (mig 493). It is fire-and-forget and cannot throw, so it cannot make this crash worse.
    reportRenderCrash(error, info.componentStack);
  }

  private handleReload = (): void => {
    // A full reload is the most reliable recovery from a render crash: re-resetting the
    // boundary's state would immediately re-render the same (still-broken) subtree and
    // crash again for a deterministic error. Reload re-fetches a clean tree.
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const locale = this.props.locale ?? 'ar';
      return (
        <div
          dir={locale === 'ar' ? 'rtl' : 'ltr'}
          style={{ maxWidth: 560, margin: '64px auto', padding: '0 20px' }}
        >
          <LxErrorState
            locale={locale}
            titleAr="حدث خطأ غير متوقع في الصفحة"
            titleEn="An unexpected error occurred"
            bodyAr="نعتذر — تعطّلت هذه الصفحة. أعد التحميل للمتابعة، وإن استمرّ الأمر فأبلغ المسؤول."
            bodyEn="Sorry — this page crashed. Reload to continue, and let your admin know if it persists."
            onRetry={this.handleReload}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
